import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../creds';
import { validateForm, validateSingleField } from '../../utils/validations';
import CustomSelect from '../common/CustomSelect';
import CustomRadio from '../common/CustomRadio';
import NewCustomInput from '../common/NewCustomInput';
import ShowPreview from './showPreview';
import { FaInfoCircle } from 'react-icons/fa';
import Div3 from './Div3';
import Div4 from './Div4';
import Div6 from './Div6';
import { initialStateData } from '../../utils/Constant';
import CustomDatePicker from '../common/CustomDatePicker'


const AdForm = ({ setSubmissions, setShowForm, editData, formRef }) => {
  const [formData, setFormData] = useState(initialStateData);
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [mockDatabase, setMockDatabase] = useState({
    categories: [],
    subcategories: [],
    qualifications: [],
    prefixes: [],
    suffixes: [],
    themes: [],
    priority: [],
    languages: [],
    sources: [],
    layouts: [],
    lines: [],
  });
  const [showCategoryInfo, setShowCategoryInfo] = useState(false);
  const categoryInfoRef = useRef(null);

  const fieldRefs = {
    agency: useRef(null),
    size: useRef(null),
    category: useRef(null),
    customCategory: useRef(null),
    subcategory: useRef(null),
    customSubcategory: useRef(null),
    hline1: useRef(null),
    content: useRef(null),
    hlinebg: useRef(null),
    contentbg: useRef(null),
    contactbg: useRef(null),
    whatsapp: useRef(''),
    mobile: useRef(''),
    email: useRef(''),
  };

  // 1. Load editData into form
  useEffect(() => {
    if (editData) {
      setFormData({
        id: editData.id || null,
        gender: editData.gender || 'Boy',
        category: editData.category || '',
        customCategory: '',
        subcategory: editData.subcategory || '',
        customSubcategory: '',
        priority: editData.priority || 'Normal',
        agency: editData.agency || '',
        language: editData.language || 'English',
        customLanguage: '',
        source: editData.source || '',
        space: editData.space || 'Box',
        size: editData.space === 'Line' ? editData.lines || '' : editData.size || '',

        hlinebg: editData.hlinebg || 'Dark',
        hline1: editData.hline1 || '',
        hline1fs: editData.hline1fs || '12px',
        hline2: editData.hline2 || '',
        hline2fs: editData.hline2fs || '',
        hline3: editData.hline3 || '',
        hline3fs: editData.hline3fs || '',

        contentbg: editData.contentbg || 'Normal',
        contentfs: editData.contentfs || '12px',
        content: editData.content || '',
        contactbg: editData.contactbg || 'Normal',
        whatsapp: editData.whatsapp || '',
        mobile: editData.mobile || '',
        email: editData.email || '',
        InsertDate: editData.insertion_date || '',
        files: [],
      });
    }
  }, [editData]);

  // 2. Clear headlines if Line is selected
  useEffect(() => {
    if (formData.space === "Line") {
      setFormData((prev) => ({
        ...prev,
        hline1: "",
        hline1fs: "",
        hline2: "",
        hline2fs: "",
        hline3: "",
        hline3fs: "",
      }));
    }
  }, [formData.space]);


 
  const fetchDropdowns = async (retry = true) => {
    try {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) throw new Error('No access token available');
      const response = await axios.get(`${API_URL}/classifieds`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      setMockDatabase(response.data);
    } catch (err) {
     setErrors({ general: err.response?.data?.detail || 'Failed to fetch dropdowns' });
    }
  };

  useEffect(() => {
    fetchDropdowns();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryInfoRef.current && !categoryInfoRef.current.contains(event.target)) {
        setShowCategoryInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If user typed something in any contact field, clear "contact" errors
    if (['whatsapp', 'mobile', 'email'].includes(name)) {
      const hasAnyContact =
        (name === 'whatsapp' ? value.trim() : formData.whatsapp?.trim()) ||
        (name === 'mobile' ? value.trim() : formData.mobile?.trim()) ||
        (name === 'email' ? value.trim() : formData.email?.trim());

      setErrors((prev) => ({
        ...prev,
        whatsapp: hasAnyContact ? null : prev.whatsapp,
        mobile: hasAnyContact ? null : prev.mobile,
        email: hasAnyContact ? null : prev.email,
        contact: hasAnyContact ? null : prev.contact,
      }));
    } else {
      // Keep your normal single-field clearing
      setErrors((prev) => ({
        ...prev,
        [name]: value.trim() ? null : prev[name],
      }));
    }
  };


  const handleCustomChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      const newErrors = validateSingleField(field, value, { ...formData, [field]: value });
      setErrors((prev) => ({
        ...prev,
        [field]: newErrors[field] || null,
        contact: newErrors.contact || null,
      }));
    },
    [formData]
  );

  // eslint-disable-next-line
  const handleHeadingChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    const newErrors = validateSingleField(name, value, {
      ...formData,
      [name]: value,
    });
    setErrors((prev) => ({
      ...prev,
      [name]: newErrors[name] || null,
    }));
  };


  const handleFontSizeChange = (lineKey) => (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [`${lineKey}fs`]: value,
    }));
    const newErrors = validateSingleField(`${lineKey}fs`, value, {
      ...formData,
      [`${lineKey}fs`]: value,
    });
    setErrors((prev) => ({
      ...prev,
      [`${lineKey}fs`]: newErrors[`${lineKey}fs`] || null,
    }));
  };


  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, files }));
  }, []);

  const handlePreviewSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      // 👇 Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus?.();
      }

      return;
    }

    setErrors({});
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus?.();
      }
      return;
    }


    const formDataToSend = new FormData();
    const customFields = ['category', 'subcategory', 'language'];
    const nullableFields = [
      'subcategory', 'whatsapp', 'mobile', 'email', 'customCategory', 'customSubcategory', 'customLanguage',
      'source', 'size', 'hline2', 'hline2fs', 'hline3', 'hline3fs',
    ];

    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'files') {
        value.forEach((file) => formDataToSend.append('files', file));
      } else if (key.startsWith('hline') && value && typeof value === 'object') {
        formDataToSend.append(key, value.text || '');
        formDataToSend.append(`${key}fs`, value.fontSize || '');
      } else if (['hlinebg', 'contentbg', 'contentfs', 'contactbg'].includes(key)) {
        formDataToSend.append(key, value || 'Normal');
      } else if (key === 'space') {
        formDataToSend.append('space', value || '');
      } else if (key === 'size') {
        formDataToSend.append('size', value || '');
      } else if (!['id', 'customCategory', 'customSubcategory', 'customLanguage'].includes(key)) {
        if (customFields.includes(key) && value === 'Other') {
          const customValue = formData[`custom${key.charAt(0).toUpperCase() + key.slice(1)}`] || '';
          formDataToSend.append(key, customValue);
        } else {
          formDataToSend.append(key, nullableFields.includes(key) && !value ? '' : value);
        }
      }
    });

    try {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) throw new Error('No access token available');
      const url = formData.id ? `${API_URL}/classifieds/${formData.id}` : `${API_URL}/classifieds`;
      const method = formData.id ? 'put' : 'post';
      const response = await axios({
        method,
        url,
        data: formDataToSend,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setSubmissions((prev) => {
        const newSubmissions = formData.id
          ? prev.map((sub) => (sub.id === formData.id ? { ...formData, id: formData.id, crn: response.data.crn } : sub))
          : [...prev, { ...formData, id: response.data.id || Date.now(), crn: response.data.crn }];
        localStorage.setItem('submissions', JSON.stringify(newSubmissions));
        return newSubmissions;
      });
      setFormData(initialStateData);
      setErrors({});
      setShowPreview(false);
      setShowForm(false);
      toast.success(formData.id ? 'Classified updated successfully' : 'Classified created successfully');
    } catch (err) {
        setErrors({ general: err.response?.data?.detail || 'Submission failed. Please try again.' });
        toast.error(`${err.response?.data?.detail}` || 'Submission failed. Please try again.' )
      }
  };

  const handleCancel = () => {
    setFormData(initialStateData);
    setErrors({});
    setShowPreview(false);
    setShowForm(false);
  };

  const toggleCategoryInfo = () => {
    setShowCategoryInfo((prev) => !prev);
  };

  return (
    <div className="p-4 sm:p-8">
      {errors.general && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md text-[clamp(12px,2vw,14px)]">
          {errors.general}
        </div>
      )}

      <div className="flex justify-center items-center">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="bg-white border border-gray-300 rounded-2xl p-4 sm:p-6 w-full max-w-[700px] mx-auto shadow-lg"
        >
          <h3 className="text-2xl text-center text-gray-900 font-semibold mb-2">{editData ? 'Edit Classified' : 'Add Classified'}</h3>
          <div className="mb-2 border-b border-gray-300 pb-2 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-gray-900 font-bold after:ml-0.5 after:text-red-500 after:content-['*'] ">Gender</span>
              <CustomRadio
                name="gender"
                options={['Boy', 'Girl']}
                value={formData.gender}
                onChange={handleChange}
                error={errors.gender}
                required
                className="flex gap-4 text-base cursor-pointer"
              />
            </div>
            <div className='w-48'>
              <CustomSelect
                label={<span>Language</span>}
                name="language"
                options={mockDatabase.languages}
                value={formData.language}
                onChange={handleChange}
                hasOther
                customValue={formData.customLanguage}
                onCustomChange={handleCustomChange('customLanguage')}
                error={errors.language || errors.customLanguage}
              />
            </div>
            <div className='w-60' ref={fieldRefs.agency}>
              <NewCustomInput
                label={<span>Agency</span>}
                name="agency"
                type="text"
                value={formData.agency}
                onChange={handleChange}
                language={formData.language}
                placeholder="Enter agency name"
                error={errors.agency}
                required
              />
            </div>
            <div ref={fieldRefs.size}>
              <span className="text-gray-900 font-bold">Space (Size)</span>
              <CustomRadio
                name="space"
                options={['Line', 'Box']}
                value={formData.space}
                onChange={handleChange}
                error={errors.space}
                className="flex gap-4 text-base cursor-pointer"
              />
              <CustomSelect
                name="size"
                options={formData.space === 'Line' ? mockDatabase.lines : mockDatabase.layouts}
                value={formData.size}
                onChange={handleChange}
                error={errors.size}
                optionName={formData.space === "Line" ? "Lines" : "Cm"}
              />
            </div>
          </div>
          <div className="mb-6 border-b border-gray-300 pb-4 flex flex-wrap justify-between">
            <div className="relative mb-4">
              <div className="flex items-center gap-2">
                <div className="flex-grow" ref={fieldRefs.category}>
                  <CustomSelect
                    label={<span>Category <span className="text-red-700">*</span></span>}
                    name="category"
                    options={mockDatabase.categories}
                    value={formData.category}
                    onChange={handleChange}
                    hasOther
                    customValue={formData.customCategory}
                    onCustomChange={handleCustomChange('customCategory')}
                    error={errors.category || errors.customCategory}
                    required
                    className="w-full"
                    optionName="category"
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleCategoryInfo}
                  onMouseEnter={() => setShowCategoryInfo(true)}
                  onMouseLeave={() => setShowCategoryInfo(false)}
                  onFocus={() => setShowCategoryInfo(true)}
                  onBlur={() => setShowCategoryInfo(false)}
                  onTouchStart={() => setShowCategoryInfo(true)}
                  className="text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 flex-shrink-0"
                  aria-label="Category information"
                >
                  <FaInfoCircle size={18} />
                </button>
              </div>
              {showCategoryInfo && (
                <div
                  ref={categoryInfoRef}
                  className="absolute z-[9999] bg-white border border-gray-300 rounded-md p-3 shadow-lg md:top-0 md:left-[calc(100%+0.5rem)] md:w-[250px] top-[100%] left-0 w-full text-sm text-gray-700"
                >
                  Select a category to classify your ad, or choose 'Other' to specify a custom category.
                </div>
              )}
            </div>
            <CustomSelect
              label={<span>Subcategory</span>}
              name="subcategory"
              options={mockDatabase.subcategories}
              value={formData.subcategory}
              onChange={handleChange}
              hasOther
              customValue={formData.customSubcategory}
              onCustomChange={handleCustomChange('customSubcategory')}
              error={errors.subcategory || errors.customSubcategory}
              optionName="subcategory"
            />
          </div>
          <div>
            {formData.space === 'Box' && (
              <Div3
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleChange={handleChange}
                handleFontSizeChange={handleFontSizeChange}
              />
            )}
          </div>
          <Div4
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            handleChange={handleChange}
            handleFontSizeChange={handleFontSizeChange}
          />
          <Div6
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            handleChange={handleChange}
          />
          <CustomSelect
            label={<span>Source</span>}
            name="source"
            options={mockDatabase.sources}
            value={formData.source}
            onChange={handleChange}
            error={errors.source}
            optionName="Source"
          />
          <div className="mb-4 mt-2">
            <label className="text-gray-900 text-md font-semibold">Upload Files</label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="block w-full border border-gray-500 rounded-md pr-3 py-2 px-2 text-lg 
                file:mr-4 file:py-2 file:px-4 
                file:border-0 file:text-xl file:font-semibold 
                file:bg-black file:text-white hover:file:bg-gray-200 hover:file:text-gray-700"
            />
            {errors.files && <div className="text-red-700 text-sm mt-1">{errors.files}</div>}
          </div>

          <CustomDatePicker
            label="inserton Date"
            name="InsertDate"
            value={formData.InsertDate}
            onChange={handleChange}
            placeholder="Select Insert date"
            error={errors.InsertDate}
          />

          <div className="flex gap-4 justify-center text-center">
            <button
              type="button"
              onClick={handlePreviewSubmit}
              className="bg-blue-600 text-white hover:bg-blue-700 font-bold py-3 px-6 rounded-lg shadow-md"
            >
              Preview
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white hover:bg-blue-700 font-bold py-3 px-6 rounded-lg shadow-md"
            >
              Save
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-600 text-white hover:bg-gray-700 font-bold py-3 px-6 rounded-lg shadow-md"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <ShowPreview
        showPreview={showPreview}
        setShowPreview={setShowPreview}
        formData={formData}
        title="Preview details"
      />
    </div>
  );
};

AdForm.propTypes = {
  setSubmissions: PropTypes.func.isRequired,
  setShowForm: PropTypes.func.isRequired,
  editData: PropTypes.object,
};

export default AdForm;